import React from 'react';
import { Box, Container, Typography, Grid, Link, Divider, IconButton } from '@mui/material';
import {
  Event as EventIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'Events', path: '/events' },
      { label: 'My RSVPs', path: '/my-rsvps' },
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'Careers', path: '/careers' },
    ],
    support: [
      { label: 'Help Center', path: '/help' },
      { label: 'Terms of Service', path: '/terms' },
      { label: 'Privacy Policy', path: '/privacy' },
    ],
  };

  const socialLinks = [
    { icon: <FacebookIcon />, href: 'https://facebook.com', label: 'Facebook' },
    { icon: <TwitterIcon />, href: 'https://twitter.com', label: 'Twitter' },
    { icon: <InstagramIcon />, href: 'https://instagram.com', label: 'Instagram' },
    { icon: <LinkedInIcon />, href: 'https://linkedin.com', label: 'LinkedIn' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'white',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" mb={2}>
              <EventIcon sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>
                9DesignEvents
              </Typography>
            </Box>
            <Typography variant="body2" color="grey.400" mb={2}>
              Your complete event management and attendance tracking solution. Create, manage, and
              track events with ease.
            </Typography>
            <Box display="flex" gap={1}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    color: 'grey.400',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                  aria-label={social.label}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Product
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {footerLinks.product.map((link) => (
                <Link
                  key={link.path}
                  component="button"
                  variant="body2"
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: 'grey.400',
                    textAlign: 'left',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'white',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Company
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {footerLinks.company.map((link) => (
                <Link
                  key={link.path}
                  component="button"
                  variant="body2"
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: 'grey.400',
                    textAlign: 'left',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'white',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Support Links */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Support
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {footerLinks.support.map((link) => (
                <Link
                  key={link.path}
                  component="button"
                  variant="body2"
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: 'grey.400',
                    textAlign: 'left',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'white',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'grey.800' }} />

        {/* Copyright */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography variant="body2" color="grey.400">
            &copy; {currentYear} The9Designs. All rights reserved.
          </Typography>
          <Box display="flex" gap={2}>
            <Link
              href="/terms"
              variant="body2"
              sx={{
                color: 'grey.400',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Terms
            </Link>
            <Link
              href="/privacy"
              variant="body2"
              sx={{
                color: 'grey.400',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Privacy
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
